import React, { FC, useState } from 'react';

import { Col, Row } from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { ChallengeDetailsBox } from 'src/components/challenge/ChallengeDetailsBox';
import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import ChallengeResourceWrapper from 'src/components/challenge/ChallengeResourceWrapper';
import TypeformComponent from 'src/components/TypeformComponent';
import { IRootReducers } from 'src/reducers';
import {
  CampaignModuleStatusEnum,
  IResourceBehaviorsEnum,
  ResourceTypeDetailEnum,
} from 'src/shared/enums';
import { IChallengeResourceRender, IUser } from 'src/shared/models';
import { ISurvey } from 'src/shared/models/survey.model';
import {
  getResourceDetail,
  getShortDateFormat,
  getSurveyResponseIfExists,
  scrollDown,
} from 'utils';

interface OwnProps extends IChallengeResourceRender {
  isChallengeComponent?: boolean;
  isCampaignComponent?: boolean;
  performAction: (idSurvey: number) => Promise<void>;
  resource?: IResourceList;
  icon?: string;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const ChallengeSurvey: FC<Props> = ({
  isChallengeComponent = true,
  isCampaignComponent,
  performAction,
  resource,
  resourceBehavior,
  user,
  icon,
}) => {
  const { formatMessage } = useIntl();
  let date: string;
  let disabled: boolean;
  let isSurveyCompleted: boolean;
  let shouldRender: boolean;

  const startDetail = getResourceDetail(
    resource,
    ResourceTypeDetailEnum.SURVEY_DATE
  );
  const endDetail = getResourceDetail(
    resource,
    ResourceTypeDetailEnum.SURVEY_END_DATE
  );
  const descriptionDetail = getResourceDetail(
    resource,
    ResourceTypeDetailEnum.SURVEY_DESCRIPTION
  );
  const buttonDetail = getResourceDetail(
    resource,
    ResourceTypeDetailEnum.SURVEY_BUTTON_TEXT
  );

  const surveyStart = moment.utc(startDetail?.value);
  const surveyEnd = moment.utc(endDetail?.value);

  const validDates = surveyStart.isValid() || surveyEnd.isValid();

  const started =
    surveyStart.isValid() && surveyStart.isSameOrBefore(moment.utc());
  const expired = surveyEnd.isValid() && surveyEnd.isSameOrBefore(moment.utc());

  switch (true) {
    case !validDates:
    // exists survey start date, but now is not on dates
    case surveyStart.isValid() && !started && !expired:
      disabled = true;
      shouldRender = resourceBehavior
        ? resourceBehavior.behavior === IResourceBehaviorsEnum.DISABLED
        : true;
      break;
    case !resourceBehavior:
    case resourceBehavior.status && started:
      shouldRender = true;
      disabled = false;
      break;
    case resourceBehavior.behavior === IResourceBehaviorsEnum.DISABLED:
      shouldRender = true;
      disabled = true;
      break;
    case resourceBehavior.behavior === IResourceBehaviorsEnum.HIDDEN:
      shouldRender = false;
      disabled = true;
      break;
  }

  const { survey } = resource;
  const { status, statusSurvey } = getSurveyResponseIfExists(survey) || {
    status: false,
    statusSurvey: CampaignModuleStatusEnum.GENERIC_NOT_DONE,
  };

  isSurveyCompleted =
    status && statusSurvey === CampaignModuleStatusEnum.GENERIC_DONE;

  switch (true) {
    case surveyStart.isValid() && surveyEnd.isValid():
      date = formatMessage(
        { id: 'page.challenge.survey.dates' },
        {
          startDate: surveyStart.format(getShortDateFormat()),
          endDate: surveyEnd.format(getShortDateFormat()),
        }
      );
      break;
    case surveyStart.isValid() && !surveyEnd.isValid():
      date = formatMessage(
        { id: 'page.challenge.survey.only-start-date' },
        {
          startDate: surveyStart.format(getShortDateFormat()),
        }
      );
      break;
    case !surveyStart.isValid() && surveyEnd.isValid():
    default:
      break;
  }

  return (
    user && (
      <Survey
        {...{
          buttonText: buttonDetail?.value,
          date,
          description: descriptionDetail?.value,
          disabled,
          expired,
          isCampaignComponent,
          isChallengeComponent,
          isSurveyCompleted,
          performAction,
          resource,
          shouldRender,
          surveyData: survey,
          title: resource?.name,
          user,
          icon,
        }}
      />
    )
  );
};

export const Survey = ({
  buttonText,
  date,
  description,
  disabled = false,
  expired,
  isCampaignComponent,
  isChallengeComponent = false,
  isSurveyCompleted,
  performAction,
  shouldRender,
  surveyData,
  title,
  user,
  icon,
}: {
  buttonText?: string;
  date?: string;
  description?: string;
  disabled?: boolean;
  expired?: boolean;
  isCampaignComponent?: boolean;
  isChallengeComponent: boolean;
  isSurveyCompleted?: boolean;
  performAction: (idSurvey: number) => Promise<void>;
  shouldRender: boolean;
  surveyData: ISurvey;
  title?: string;
  user: IUser;
  icon?: string;
}) => {
  const [showSurvey, setShowSurvey] = useState(false);
  const { formatMessage, locale } = useIntl();

  if (!user || !surveyData) return <React.Fragment />;
  const { title: surveyTitle, content, image, score, idSurvey } = surveyData;

  const completeSurvey = async () => {
    await performAction(idSurvey);
    setShowSurvey(false);
  };

  const handleShowSurvey = () => {
    scrollDown(`survey-form-${idSurvey}`);
  };

  const component = shouldRender && (
    <Row
      justify="center"
      // TODO: use value from redux-store: similar to "ChallengeQuiz"
      className={`challengeDetail--quiz ${
        isCampaignComponent ? 'challengeDetail--campaign-quiz' : 'old-quiz'
      }`}
      id="challengeDetail--survey"
    >
      <Col span={24}>
        <ChallengeDetailsBox
          allowRetry={false}
          completed={isSurveyCompleted}
          date={date}
          description={content}
          detailTitle={buttonText}
          disabled={disabled}
          expired={expired}
          handleShowSurvey={handleShowSurvey}
          image={image}
          isSurvey={true}
          points={score?.points || 0}
          setShowComponent={setShowSurvey}
          showQuiz={showSurvey}
          title={surveyTitle}
          isCampaignComponent={isCampaignComponent}
        />
        <div id={`survey-form-${idSurvey}`}>
          <TypeformComponent
            {...{
              user: user,
              showSurvey,
              surveyData,
              completeSurvey,
            }}
          />
        </div>
      </Col>
    </Row>
  );

  return (
    <ChallengeResourceWrapper
      children={component}
      description={description}
      icon={isChallengeComponent && icon}
      bottomLabel={
        isCampaignComponent &&
        isSurveyCompleted && {
          label: 'page.challenge.congratulations-survey-completed',
          style: 'completed',
        }
      }
      points={
        isCampaignComponent &&
        formatMessage(
          { id: 'page.challenge.plus-sign-{points}-points' },
          { points: Number(score?.points).toLocaleString(locale) }
        )
      }
      styleClass="challengeDetail--Survey"
      title={title}
    />
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChallengeSurvey);
